import React, { createContext, useContext, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';

// Créer le contexte
const ModalContext = createContext();

/**
 * Modal porté dans le DOM avec portal React
 * @param {Object} props - Propriétés du composant
 * @param {string} props.id - ID optionnel du modal
 * @param {string} props.className - Classes CSS additionnelles
 * @param {ReactNode} props.children - Contenu du modal
 * @param {Function} props.onClose - Fonction appelée à la fermeture du modal
 * @param {boolean} props.layout - Afficher ou non le layout standard (true par défaut)
 * @param {string} props.size - Taille du modal ('sm', 'md', 'lg', 'xl', '2xl', 'full')
 */
const PortalModal = forwardRef(({ id, className, children, onClose, layout = true, size = 'md' }, ref) => {
  const [isOpen, setIsOpen] = useState(false);

  // Déterminer la classe CSS de taille
  const getSizeClass = () => {
    switch (size) {
      case 'sm': return 'max-w-sm md:max-w-md';
      case 'md': return 'max-w-md md:max-w-2xl';
      case 'lg': return 'max-w-lg md:max-w-3xl';
      case 'xl': return 'max-w-3xl md:max-w-5xl';
      case '2xl': return 'max-w-5xl md:max-w-7xl';
      case 'full': return 'max-w-full w-[95%] md:w-[98%]';
      default: return 'max-w-md md:max-w-2xl';
    }
  };

  useImperativeHandle(ref, () => ({
    showModal: () => {
      setIsOpen(true);
    },
    hideModal: () => {
      setIsOpen(false);
      onClose && onClose();
    },
    isOpen: () => {
      return isOpen;
    }
  }));

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <ModalContext.Provider value={{ hideModal: ref.current.hideModal, showModal: ref.current.showModal }}>
      <dialog onClick={(e) => e.stopPropagation()} className="modal modal-open" id={id}>
        <div className={`modal-box relative ${getSizeClass()} ${className ? className : ''} ${!layout ? 'p-0 bg-transparent border-none shadow-none' : ''}`}>
          {layout && <button onClick={ref.current.hideModal} className='btn btn-sm btn-circle btn-ghost absolute right-2 top-2'>✕</button>}
          {children}
        </div>
        <form method="dialog" className="modal-backdrop" onClick={ref.current.hideModal}>
          <button type='button' className='bg-transparent'>x</button>
        </form>
      </dialog>
    </ModalContext.Provider>,
    document.body
  );
});

export default React.memo(PortalModal);

// Exporter un hook pour utiliser le contexte
export const usePortalModal = () => useContext(ModalContext);
